<template>
  <div class="show_all_content_wrapper">
    <main>
      <div class="table_title_wrapper">
        <div class="title_text_wrapper">
          <h5>{{ $t("PLACEHOLDERS.Overall_statistics") }}</h5>
        </div>
      </div>

      <div class="statics mt-4">
        <div v-if="isEmployeeView" class="employee_dashboard">
          <div class="employee_identity_card">
            <div class="identity_icon">
              <i class="fas fa-id-badge"></i>
            </div>

            <div class="identity_details">
              <h6>{{ employeeProfile.name || "-" }}</h6>
              <p>
                <i class="fas fa-envelope"></i>
                <span>{{ employeeProfile.email || "-" }}</span>
              </p>
              <p>
                <i class="fas fa-phone"></i>
                <span>{{ employeeProfile.phone || "-" }}</span>
              </p>
              <p>
                <i class="fas fa-fingerprint"></i>
                <span>{{ employeeProfile.code || "-" }}</span>
              </p>
              <p>
                <i class="fas fa-toggle-on"></i>
                <span>{{ employeeProfileStatus }}</span>
              </p>
            </div>
          </div>

          <div
            class="employee_metric_section"
            v-for="section in employeeMetricGroups"
            :key="section.sectionKey"
          >
            <div class="section_header">
              <h6>{{ $t(`PLACEHOLDERS.${section.titleKey}`) }}</h6>
            </div>

            <div class="row">
              <div
                class="col-lg-4 col-md-6 col-12"
                v-for="item in section.items"
                :key="item.key"
              >
                <div class="box employee_metric_box">
                  <div class="icon">
                    <i :class="item.icon"></i>
                  </div>

                  <div class="info_box">
                    <span>{{ $t(`PLACEHOLDERS.${item.labelKey}`) }}</span>
                    <p class="number_box">{{ item.value }}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div v-else class="row">
          <div
            class="col-lg-4 col-md-6 col-12"
            v-for="(value, key) in adminStatCards"
            :key="'i' + key"
          >
            <div class="box stat-card" @click="navigateStatPage(key)">
              <div class="icon">
                <i :class="getIcon(key)"></i>
              </div>

              <div class="info_box">
                <span>{{ $t(`PLACEHOLDERS.${key}`) }}</span>
                <p class="number_box">{{ value }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </main>
  </div>
</template>

<script>
import { mapGetters } from "vuex";

export default {
  name: "HomePage",

  data() {
    return {
      statics: {},
      employeeProfileData: {},
      employeeIconMap: {
        balance: "fas fa-wallet",
        client_booking_commission: "fas fa-percent",
        provider_registration_commission: "fas fa-percentage",
        count_client_bookings: "fas fa-calendar-check",
        count_provider_registrations: "fas fa-user-plus",
        total_client_bookings: "fas fa-receipt",
        total_provider_registrations: "fas fa-file-signature",
      },
    };
  },

  computed: {
    ...mapGetters({
      authenticatedUserData: "AuthenticationModule/getAuthenticatedUserData",
    }),

    normalizedType() {
      return (this.statics?.type || this.authenticatedUserData?.type || "")
        .toString()
        .trim()
        .toLowerCase();
    },

    isEmployeeView() {
      return this.normalizedType === "employee";
    },

    employeeProfile() {
      return {
        name:
          this.employeeProfileData?.name ||
          this.statics?.name ||
          this.authenticatedUserData?.name,
        email:
          this.employeeProfileData?.email ||
          this.statics?.email ||
          this.authenticatedUserData?.email,
        phone:
          this.employeeProfileData?.phone_number ||
          this.employeeProfileData?.mobile ||
          this.employeeProfileData?.phone ||
          this.authenticatedUserData?.phone ||
          "",
        code: this.employeeProfileData?.code || this.statics?.code,
        is_active:
          this.employeeProfileData?.is_active ?? this.statics?.is_active,
      };
    },

    employeeProfileStatus() {
      return this.$t(
        `PLACEHOLDERS.${
          this.employeeProfile.is_active ? "is_active" : "inactive"
        }`
      );
    },

    adminStatCards() {
      const ignored = new Set([
        "type",
        "name",
        "email",
        "phone",
        "phone_number",
        "mobile",
        "code",
        "is_active",
        "avatar",
        "id",
      ]);
      return Object.keys(this.statics || {}).reduce((cards, key) => {
        if (!ignored.has(key)) cards[key] = this.statics[key];
        return cards;
      }, {});
    },

    employeeMetricGroups() {
      return [
        {
          sectionKey: "financial",
          titleKey: "employee_financial_metrics",
          items: [
            this.getEmployeeMetricItem("balance"),
            this.getEmployeeMetricItem("total_client_bookings"),
            this.getEmployeeMetricItem("total_provider_registrations"),
          ],
        },
        {
          sectionKey: "commission",
          titleKey: "employee_commission_metrics",
          items: [
            this.getEmployeeMetricItem("client_booking_commission"),
            this.getEmployeeMetricItem("provider_registration_commission"),
          ],
        },
        {
          sectionKey: "activity",
          titleKey: "employee_activity_metrics",
          items: [
            this.getEmployeeMetricItem("count_client_bookings"),
            this.getEmployeeMetricItem("count_provider_registrations"),
          ],
        },
      ];
    },
  },

  methods: {
    getEmployeeMetricItem(key) {
      return {
        key,
        labelKey: key,
        icon: this.employeeIconMap[key] || "fas fa-chart-bar",
        value: this.statics?.[key] ?? 0,
      };
    },

    getIcon(key) {
      const icons = {
        clients: "fas fa-users",
        providers: "fas fa-user-tie",
        employee: "fas fa-user-shield",
        vehivle: "fas fa-car",
        pending_review_vehivles: "fas fa-clock",
        current_wek_booking: "fas fa-calendar-week",
        confirmed_booking: "fas fa-calendar-check",
        open_complains: "fas fa-exclamation-circle",
      };
      return icons[key] || "fas fa-chart-bar";
    },

    navigateStatPage(key) {
      // Employee/marketer stats are display-only.
      if (this.isEmployeeView) return;
      const route = this.getStatRoute(key);
      if (!route) {
        return;
      }
      this.$router.push(route);
    },

    getStatRoute(key) {
      const routes = {
        clients: { name: "AllClients" },
        providers: { name: "AllProviders" },
        employee: { name: "AllUsers", query: { type: "employee" } },
        vehivle: { name: "AllVehicles" },
        pending_review_vehivles: { name: "AllvehiclesmanagementHome" },
        current_wek_booking: { name: "AllBookings" },
        confirmed_booking: { name: "AllBookings" },
        open_complains: { name: "ComplaintsList" },
        balance: { name: "AllUsers", query: { type: "employee" } },
        total_client_bookings: { name: "AllBookings" },
        total_provider_registrations: { name: "AllProviders" },
        client_booking_commission: { name: "AllBookings" },
        provider_registration_commission: { name: "AllProviders" },
        count_client_bookings: { name: "AllBookings" },
        count_provider_registrations: { name: "AllProviders" },
      };
      return routes[key] || null;
    },

    async getStatics() {
      try {
        const res = await this.$axios({
          method: "GET",
          url: "statistics",
        });
        this.statics = res.data.data;
        if (this.isEmployeeView) {
          await this.getEmployeeProfile();
        }
      } catch (error) {
        console.error(
          "Error fetching statistics:",
          error?.response?.data?.message
        );
      }
    },

    async getEmployeeProfile() {
      try {
        const res = await this.$axios({
          method: "GET",
          url: "auth/profile",
        });

        this.employeeProfileData = res.data.data?.user || {};

        this.$store.dispatch("AuthenticationModule/setAuthenticatedUserData", {
          name: this.employeeProfileData.name,
          email: this.employeeProfileData.email,
          phone:
            this.employeeProfileData.phone_number ||
            this.employeeProfileData.mobile ||
            this.employeeProfileData.phone,
          type: this.employeeProfileData.type,
          image: this.employeeProfileData.avatar,
        });
      } catch (error) {
        console.error(
          "Error fetching employee profile:",
          error?.response?.data?.message
        );
      }
    },
  },

  mounted() {
    this.getStatics();
  },
};
</script>

<style lang="scss" scoped>
.show_all_content_wrapper {
  .statics {
    .employee_dashboard {
      .employee_identity_card {
        background: linear-gradient(135deg, #1f5ca9, #2f87e7);
        border-radius: 8px;
        padding: 20px;
        margin-bottom: 16px;
        display: flex;
        align-items: center;
        gap: 18px;

        .identity_icon {
          width: 74px;
          height: 74px;
          border-radius: 50%;
          background: rgba(255, 255, 255, 0.2);
          display: flex;
          align-items: center;
          justify-content: center;

          i {
            color: #fff;
            font-size: 30px;
          }
        }

        .identity_details {
          h6 {
            color: #fff;
            margin: 0 0 8px;
            font-size: 20px;
            font-weight: 700;
          }

          p {
            margin: 0 0 5px;
            color: #f2f7ff;
            font-size: 14px;
            display: flex;
            align-items: center;
            gap: 8px;

            i {
              min-width: 16px;
              font-size: 13px;
            }
          }
        }
      }

      .employee_metric_section {
        margin-bottom: 16px;

        .section_header {
          margin: 0 0 10px;

          h6 {
            background: #f4f7ff;
            color: #17427a;
            border-inline-start: 4px solid #2f87e7;
            border-radius: 4px;
            padding: 10px 12px;
            font-size: 15px;
            font-weight: 700;
            margin: 0;
          }
        }
      }
    }

    .box {
      background: var(--main_theme_clr);
      padding: 25px;
      margin-bottom: 10px;
      border-radius: 5px;
      overflow: hidden;
      display: flex;
      justify-content: space-between;

      transition: all 0.5s;

      &:hover {
        background: #fff;
        box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);

        .icon {
          i {
            color: var(--main_theme_clr);
          }
        }
        .info_box {
          span,
          p.number_box {
            color: #000;
          }
        }
      }

      .icon {
        height: 60px;
        min-width: 60px;
        width: 60px;
        border-radius: 60px;
        background: #fff;
        display: flex;
        align-items: center;
        justify-content: center;

        i {
          font-size: 25px;
          color: #000;
        }
      }

      .info_box {
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: flex-end;

        span {
          font-size: 19px;
          color: #fff;
        }

        p.number_box {
          font-size: 30px;
          color: #fff;
          font-weight: bold;
        }
      }
    }

    .employee_metric_box {
      border: 1px solid rgba(47, 135, 231, 0.22);
      cursor: default;

      .info_box {
        span {
          font-size: 15px;
          line-height: 1.4;
        }

        p.number_box {
          color: #fff;
        }
      }
    }

    .stat-card {
      cursor: pointer;
    }
  }
}
.show_all_content_wrapper .statics .box .info_box {
  align-items: flex-start !important;
  width: 100% !important;
  padding-inline-start: 5% !important;
}

@media (max-width: 767px) {
  .show_all_content_wrapper
    .statics
    .employee_dashboard
    .employee_identity_card {
    flex-direction: column;
    align-items: flex-start;
  }
}
</style>
